import { useState, type ReactNode } from 'react';
import { Icon } from './hbs/Icon';

interface CollapsibleFamilySectionProps {
  /** DOM id for the expand/collapse header. Sections repeat, so the parent
   *  derives this from the family's org id rather than its display name. */
  id?: string;
  familyName: string;
  /** When true the section is always open — used while a search query is active. */
  forceExpanded?: boolean;
  children: ReactNode;
}

/** A collapsed-by-default group of Family Safe items, headed "Family Safe - <name>". */
export function CollapsibleFamilySection({
  id,
  familyName,
  forceExpanded = false,
  children,
}: CollapsibleFamilySectionProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const expanded = forceExpanded || open;

  return (
    // Bordered container so the family group is visibly distinct from
    // personal items above/below and the end of the section is obvious.
    <section className="flex flex-col border border-hbs-border rounded-hbs-sm overflow-hidden">
      <button
        id={id}
        type="button"
        aria-expanded={expanded}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-2 py-1.5 text-left bg-hbs-surface-alt/40 hover:bg-hbs-surface-alt"
      >
        <Icon
          name={expanded ? 'chevronDown' : 'chevronRight'}
          size={16}
          className="text-hbs-text-mute"
        />
        <span className="text-[12px] font-semibold text-hbs-text-mute">
          Family Safe - {familyName}
        </span>
      </button>
      {expanded && (
        <ul className="flex flex-col gap-px border-t border-hbs-border">
          {children}
        </ul>
      )}
    </section>
  );
}
